import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  useTheme,
  Typography,
  Box,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  alpha
} from '@mui/material';
import Avatar from "@mui/material/Avatar";
import { AppsOutlined, InsertDriveFileOutlined, NewReleasesOutlined, UploadFileOutlined } from '@mui/icons-material';

import { useApplicationContext } from '../../context/application';
import { useAuthContext } from '../../context/auth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDialogStyles from '../../hooks/useDialogStyles';
import { formatFileDate, formatFileSize } from '../../utils/utils';
import { AddButton, DialogHeader, FormSection, SubmitButton, SwitchField } from '../dialogKit';

const Add = ({ applications, appTypes } ) => {
  const theme = useTheme();

  const axiosPrivate = useAxiosPrivate();
  const { dispatch } = useApplicationContext();
  const { auth } = useAuthContext();
  const ds = useDialogStyles();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [selectedAppType, setSelectedAppType] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFileSize, setSelectedFileSize] = useState('N/A');
  const [selectedFileCreatedAt, setSelectedFileCreatedAt] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [iconPreview, setIconPreview] = useState(null);
  const [active, setActive] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const versionRef = useRef('');
  const fileInputRef = useRef(null);

  const closeDialog = () => {
    if (isUploading) return;
    setShow(false);
    setError(null);
  };

  const handleAdd = async () => {
    if (!auth) {
      setError('You must be logged in');
      return;
    }

    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();

    formData.append("appType", selectedAppType);
    formData.append("version", versionRef.current.value);
    formData.append("file", selectedFile);
    if (selectedFileCreatedAt) {
      formData.append("fileCreatedAt", selectedFileCreatedAt);
    }
    formData.append("active", active);

    try {
      const response = await axiosPrivate.post(
        '/api/applications', formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
            }
          },
        }
      );

      dispatch({ type: 'CREATE_APPLICATION', payload: response.data });

      const iconPath = response.data.iconPath;
      if (iconPath) {
        setIconPreview(
            `${process.env.REACT_APP_SERVER_URL}${iconPath}?t=${Date.now()}`
        );
      } else {
          setIconPreview(null);
      }

      setError(null);
      setShow(false);
    } catch (error) {
      setError(error.response?.data.error || 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleOpenFileDialog = () => {
    if (!isUploading) fileInputRef.current?.click();
  };

  const pickFile = (file) => {
    if (!file) return;
    setSelectedFileName(file.name);
    setSelectedFileSize(formatFileSize(file.size));
    setSelectedFileCreatedAt(
      new Date(file.lastModified).toISOString()
    );
    setSelectedFile(file);
  };

  const handleFileSelection = (event) => {
    pickFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!isUploading) pickFile(e.dataTransfer.files?.[0]);
  };

  const resetForm = () => {
    setError(null);
    setSelectedAppType('');
    setSelectedFileName('');
    setSelectedFileSize('N/A');
    setSelectedFileCreatedAt(null);
    setSelectedFile(null);
    setIsUploading(false);
    setUploadProgress(0);
    setIconPreview(null);
    setActive(false);
    setDragOver(false);
    if (versionRef.current) versionRef.current.value = '';
  };

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, applications]);

  return (
    <>
      <AddButton label="Add" onClick={() => setShow(true)} />

      <Dialog
        open={show}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: ds.paperSx }}
      >
        <DialogHeader
          icon={AppsOutlined}
          title="New application"
          subtitle="Upload a new version for users to download"
          onClose={closeDialog}
          disabled={isUploading}
        />

        <DialogContent sx={ds.contentSx}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 140px' }, gap: 2 }}>
            <FormSection icon={NewReleasesOutlined} title="Release" />

            <FormControl fullWidth size="small" sx={ds.fieldSx}>
              <InputLabel>Application type</InputLabel>
              <Select
                label="Application type"
                value={selectedAppType}
                onChange={(e) => setSelectedAppType(e.target.value)}
                MenuProps={ds.menuProps}
              >
                {appTypes?.map((appType) => (
                  <MenuItem key={appType._id} value={appType._id}>
                    {appType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Version"
              placeholder="1.0.0"
              inputRef={versionRef}
              autoComplete="off"
              sx={ds.fieldSx}
            />

            <SwitchField
              label="Active"
              checked={active}
              onChange={setActive}
              onText="Offered to users for download"
              offText="Uploaded, but not offered yet"
              sx={{ gridColumn: '1 / -1' }}
            />

            <FormSection icon={InsertDriveFileOutlined} title="File" />
          </Box>

          {/* File: click or drop */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelection}
          />

          <Box
            role="button"
            tabIndex={0}
            onClick={handleOpenFileDialog}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenFileDialog(); } }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            sx={{
              mt: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: isUploading ? 'default' : 'pointer',
              borderRadius: 2,
              border: `1px dashed ${dragOver || selectedFileName ? alpha(accent, 0.7) : theme.palette.divider}`,
              backgroundColor: dragOver
                ? alpha(accent, 0.12)
                : isDark ? 'rgba(255,255,255,0.03)' : alpha(accent, 0.05),
              transition: 'background-color .15s, border-color .15s',
              '&:hover': { borderColor: alpha(accent, 0.7) },
            }}
          >
            {iconPreview ? (
              <Avatar src={iconPreview} alt="" variant="rounded" sx={{ width: 44, height: 44 }} />
            ) : (
              <Avatar
                variant="rounded"
                sx={{ width: 44, height: 44, color: accent, backgroundColor: alpha(accent, 0.12) }}
              >
                <InsertDriveFileOutlined />
              </Avatar>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                noWrap
                sx={{ fontWeight: 600, color: selectedFileName ? 'text.primary' : 'text.secondary' }}
              >
                {selectedFileName || (dragOver ? 'Drop the file here' : 'Click or drop a file here')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                Size: {selectedFileSize}
                {'  ·  '}
                Created: {selectedFileCreatedAt ? formatFileDate(selectedFileCreatedAt) : 'N/A'}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileOutlined />}
              onClick={(e) => { e.stopPropagation(); handleOpenFileDialog(); }}
              disabled={isUploading}
              sx={{
                flexShrink: 0,
                color: accent,
                borderColor: alpha(accent, 0.6),
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { borderColor: accent, backgroundColor: alpha(accent, 0.12) },
              }}
            >
              {selectedFileName ? 'Change' : 'Choose file'}
            </Button>
          </Box>

          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Uploading {selectedFileName}
                </Typography>
                <Typography variant="body2" sx={{ color: accent, fontWeight: 700 }}>
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 6,
                  borderRadius: 5,
                  backgroundColor: alpha(accent, 0.15),
                  '& .MuiLinearProgress-bar': { backgroundColor: accent },
                }}
              />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={ds.actionsSx}>
          <Button onClick={closeDialog} disabled={isUploading} sx={ds.cancelButtonSx}>
            Cancel
          </Button>
          <SubmitButton
            label="Add Application"
            busyLabel={`Uploading… ${uploadProgress}%`}
            busy={isUploading}
            onClick={handleAdd}
            icon={<UploadFileOutlined />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Add;
